import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { NS } from '@ownyou/shared-types';
import { useAuth } from './AuthContext';
import { useStore } from './StoreContext';

type SyncStatus = 'idle' | 'syncing' | 'error' | 'offline';
type SyncMode = 'local-only' | 'peer-sync';

interface SyncState {
  status: SyncStatus;
  lastSynced: Date | null;
  pendingChanges: number;
  connectedDevices: number;
  error: string | null;
  /** Whether wallet-derived encryption keys are available for sync */
  keysReady: boolean;
  /** Current sync mode: 'local-only' until OrbitDB is integrated (Sprint 17+) */
  syncMode: SyncMode;
}

interface SyncContextValue extends SyncState {
  sync: () => Promise<void>;
  pauseSync: () => void;
  resumeSync: () => void;
}

const SyncContext = createContext<SyncContextValue | null>(null);

interface SyncProviderProps {
  children: ReactNode;
}

export function SyncProvider({ children }: SyncProviderProps) {
  const { isAuthenticated, wallet, keys } = useAuth();
  const { store, isReady: storeReady } = useStore();
  const userId = wallet?.address ?? 'anonymous';

  const [state, setState] = useState<SyncState>({
    status: 'idle',
    lastSynced: null,
    pendingChanges: 0,
    connectedDevices: 1,
    error: null,
    keysReady: false,
    syncMode: 'local-only',
  });

  const [isPaused, setIsPaused] = useState(false);

  // Initialize sync when authenticated with wallet-derived keys
  useEffect(() => {
    if (!isAuthenticated || !wallet) {
      setState(prev => ({
        ...prev,
        status: 'idle',
        connectedDevices: 0,
        keysReady: false,
      }));
      return;
    }

    // Sprint 15: Use wallet-derived sync encryption key from AuthContext
    // Real OrbitDB sync is deferred to Sprint 17+; for now, set local-only mode
    const hasKeys = !!keys?.syncKey;

    setState(prev => ({
      ...prev,
      status: 'idle',
      connectedDevices: 1,
      lastSynced: new Date(),
      keysReady: hasKeys,
      syncMode: 'local-only',
    }));

    // Store sync configuration to LangGraph Store so other components can check
    if (hasKeys && store && storeReady) {
      store.put(NS.syncState(userId), 'sync-config', {
        keysReady: true,
        syncMode: 'local-only' as SyncMode,
        walletAddress: wallet.address,
        initializedAt: new Date().toISOString(),
      }).catch((err: unknown) => {
        console.warn('[SyncContext] Failed to persist sync config:', err);
      });
      console.log('[SyncContext] Sync initialized with wallet-derived keys (local-only mode)');
    }

    // Setup periodic sync (every 30 seconds)
    const syncInterval = setInterval(() => {
      if (!isPaused) {
        performSync();
      }
    }, 30000);

    return () => clearInterval(syncInterval);
  }, [isAuthenticated, wallet, keys, isPaused, store, storeReady, userId]);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => {
      setState(prev => ({
        ...prev,
        status: prev.status === 'offline' ? 'idle' : prev.status,
      }));
    };

    const handleOffline = () => {
      setState(prev => ({
        ...prev,
        status: 'offline',
      }));
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Check initial state
    if (!navigator.onLine) {
      setState(prev => ({ ...prev, status: 'offline' }));
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const performSync = useCallback(async () => {
    if (!isAuthenticated || isPaused) return;

    setState(prev => ({ ...prev, status: 'syncing', error: null }));

    try {
      // In production, this would:
      // 1. Push local changes to OrbitDB
      // 2. Pull remote changes
      // 3. Resolve any CRDT conflicts

      // Mock: Simulate sync delay
      await new Promise(resolve => setTimeout(resolve, 1000));

      setState(prev => ({
        ...prev,
        status: 'idle',
        lastSynced: new Date(),
        pendingChanges: 0,
      }));
    } catch (error) {
      console.error('Sync failed:', error);
      setState(prev => ({
        ...prev,
        status: 'error',
        error: error instanceof Error ? error.message : 'Sync failed',
      }));
    }
  }, [isAuthenticated, isPaused]);

  const sync = useCallback(async () => {
    await performSync();
  }, [performSync]);

  const pauseSync = useCallback(() => {
    setIsPaused(true);
  }, []);

  const resumeSync = useCallback(() => {
    setIsPaused(false);
    // Trigger immediate sync after resume
    performSync();
  }, [performSync]);

  const value: SyncContextValue = {
    ...state,
    sync,
    pauseSync,
    resumeSync,
  };

  return <SyncContext.Provider value={value}>{children}</SyncContext.Provider>;
}

export function useSync() {
  const context = useContext(SyncContext);
  if (!context) {
    throw new Error('useSync must be used within a SyncProvider');
  }
  return context;
}
